// src/services/notification.service.js
import { db } from "@/firebase";
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { Logger } from "@/utils/logger";
import { FeatureFlags } from "@/config/features";

/**
 * Notification types and their default preferences
 */
export const NOTIFICATION_TYPES = {
  // Lesson notifications - new status system
  LESSON_REQUESTED: "lesson_requested",
  LESSON_REQUEST_RECEIVED: "lesson_request_received",
  LESSON_CONFIRMED: "lesson_confirmed",
  LESSON_REJECTED: "lesson_rejected",
  LESSON_COMPLETED: "lesson_completed",
  LESSON_REVIEW_PROMPT: "lesson_review_prompt",
  CANCELLATION_REQUESTED: "cancellation_requested",
  REFUND_APPROVED: "refund_approved",
  REFUND_REJECTED: "refund_rejected",

  // Legacy lesson notifications - kept for backwards compatibility
  LESSON_BOOKED: "lesson_booked",
  LESSON_CANCELLED: "lesson_cancelled",
  LESSON_REMINDER_24H: "lesson_reminder_24h",
  LESSON_REMINDER_2H: "lesson_reminder_2h",
  LESSON_REMINDER_30M: "lesson_reminder_30m",
  MEETING_LINK_ADDED: "meeting_link_added",
  LESSON_RESCHEDULED: "lesson_rescheduled",

  // Payment notifications
  PAYMENT_SUCCESSFUL: "payment_successful",
  PAYMENT_FAILED: "payment_failed",
  REFUND_PROCESSED: "refund_processed",
  PROMOTIONAL_DISCOUNT: "promotional_discount",

  // Communication notifications
  NEW_MESSAGE: "new_message",
  CONVERSATION_STARTED: "conversation_started",

  // Account notifications
  WELCOME: "welcome",
  PROFILE_APPROVED: "profile_approved",
  ACCOUNT_VERIFICATION: "account_verification",

  // Marketing notifications
  FEATURE_ANNOUNCEMENT: "feature_announcement",
  SEASONAL_PROMOTION: "seasonal_promotion",
  INACTIVE_USER_ENGAGEMENT: "inactive_user_engagement",
};

/**
 * Default notification preferences for new users
 */
const DEFAULT_PREFERENCES = {
  email: {
    // New status system lesson notifications (critical - always enabled)
    [NOTIFICATION_TYPES.LESSON_REQUESTED]: true,
    [NOTIFICATION_TYPES.LESSON_REQUEST_RECEIVED]: true,
    [NOTIFICATION_TYPES.LESSON_CONFIRMED]: true,
    [NOTIFICATION_TYPES.LESSON_REJECTED]: true,
    [NOTIFICATION_TYPES.LESSON_COMPLETED]: true,
    [NOTIFICATION_TYPES.LESSON_REVIEW_PROMPT]: true,
    [NOTIFICATION_TYPES.CANCELLATION_REQUESTED]: true,
    [NOTIFICATION_TYPES.REFUND_APPROVED]: true,
    [NOTIFICATION_TYPES.REFUND_REJECTED]: true,

    // Legacy lesson notifications (critical - always enabled)
    [NOTIFICATION_TYPES.LESSON_BOOKED]: true,
    [NOTIFICATION_TYPES.LESSON_CANCELLED]: true,
    [NOTIFICATION_TYPES.LESSON_REMINDER_24H]: true,
    [NOTIFICATION_TYPES.LESSON_REMINDER_2H]: true,
    [NOTIFICATION_TYPES.LESSON_REMINDER_30M]: false, // User can opt-in
    [NOTIFICATION_TYPES.MEETING_LINK_ADDED]: true,
    [NOTIFICATION_TYPES.LESSON_RESCHEDULED]: true,

    // Payment notifications (critical - always enabled)
    [NOTIFICATION_TYPES.PAYMENT_SUCCESSFUL]: true,
    [NOTIFICATION_TYPES.PAYMENT_FAILED]: true,
    [NOTIFICATION_TYPES.REFUND_PROCESSED]: true,
    [NOTIFICATION_TYPES.PROMOTIONAL_DISCOUNT]: true,

    // Communication notifications (user controlled)
    [NOTIFICATION_TYPES.NEW_MESSAGE]: true,
    [NOTIFICATION_TYPES.CONVERSATION_STARTED]: true,

    // Account notifications (important - enabled by default)
    [NOTIFICATION_TYPES.WELCOME]: true,
    [NOTIFICATION_TYPES.PROFILE_APPROVED]: true,
    [NOTIFICATION_TYPES.ACCOUNT_VERIFICATION]: true,

    // Marketing notifications (opt-in by default)
    [NOTIFICATION_TYPES.FEATURE_ANNOUNCEMENT]: false,
    [NOTIFICATION_TYPES.SEASONAL_PROMOTION]: false,
    [NOTIFICATION_TYPES.INACTIVE_USER_ENGAGEMENT]: false,
  },
  frequency: "immediate", // immediate, daily, weekly
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  quietHours: {
    enabled: false,
    start: "22:00",
    end: "08:00",
  },
};

/**
 * Notification categories for grouping
 */
export const NOTIFICATION_CATEGORIES = {
  CRITICAL: "critical", // Lessons, payments - cannot be disabled
  IMPORTANT: "important", // Account updates, messages
  OPTIONAL: "optional", // Marketing, features
};

/**
 * Map notification types to categories
 */
const NOTIFICATION_CATEGORY_MAP = {
  // New status system notifications
  [NOTIFICATION_TYPES.LESSON_REQUESTED]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.LESSON_REQUEST_RECEIVED]:
    NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.LESSON_CONFIRMED]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.LESSON_REJECTED]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.LESSON_COMPLETED]: NOTIFICATION_CATEGORIES.IMPORTANT,
  [NOTIFICATION_TYPES.LESSON_REVIEW_PROMPT]: NOTIFICATION_CATEGORIES.IMPORTANT,
  [NOTIFICATION_TYPES.CANCELLATION_REQUESTED]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.REFUND_APPROVED]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.REFUND_REJECTED]: NOTIFICATION_CATEGORIES.CRITICAL,

  // Legacy notifications
  [NOTIFICATION_TYPES.LESSON_BOOKED]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.LESSON_CANCELLED]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.LESSON_REMINDER_24H]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.LESSON_REMINDER_2H]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.LESSON_REMINDER_30M]: NOTIFICATION_CATEGORIES.IMPORTANT,
  [NOTIFICATION_TYPES.MEETING_LINK_ADDED]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.LESSON_RESCHEDULED]: NOTIFICATION_CATEGORIES.CRITICAL,

  [NOTIFICATION_TYPES.PAYMENT_SUCCESSFUL]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.PAYMENT_FAILED]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.REFUND_PROCESSED]: NOTIFICATION_CATEGORIES.CRITICAL,
  [NOTIFICATION_TYPES.PROMOTIONAL_DISCOUNT]: NOTIFICATION_CATEGORIES.IMPORTANT,

  [NOTIFICATION_TYPES.NEW_MESSAGE]: NOTIFICATION_CATEGORIES.IMPORTANT,
  [NOTIFICATION_TYPES.CONVERSATION_STARTED]: NOTIFICATION_CATEGORIES.IMPORTANT,

  [NOTIFICATION_TYPES.WELCOME]: NOTIFICATION_CATEGORIES.IMPORTANT,
  [NOTIFICATION_TYPES.PROFILE_APPROVED]: NOTIFICATION_CATEGORIES.IMPORTANT,
  [NOTIFICATION_TYPES.ACCOUNT_VERIFICATION]: NOTIFICATION_CATEGORIES.CRITICAL,

  [NOTIFICATION_TYPES.FEATURE_ANNOUNCEMENT]: NOTIFICATION_CATEGORIES.OPTIONAL,
  [NOTIFICATION_TYPES.SEASONAL_PROMOTION]: NOTIFICATION_CATEGORIES.OPTIONAL,
  [NOTIFICATION_TYPES.INACTIVE_USER_ENGAGEMENT]:
    NOTIFICATION_CATEGORIES.OPTIONAL,
};

export class NotificationService {
  /**
   * Get user notification preferences
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - User preferences
   */
  static async getUserPreferences(userId) {
    try {
      Logger.log(
        "info",
        "Fetching user notification preferences",
        { userId },
        "NotificationService"
      );

      const prefsDoc = await getDoc(
        doc(db, "users", userId, "settings", "notifications")
      );

      if (prefsDoc.exists()) {
        const prefs = prefsDoc.data();
        // Merge with defaults to ensure all new notification types are covered
        return {
          ...DEFAULT_PREFERENCES,
          ...prefs,
          email: {
            ...DEFAULT_PREFERENCES.email,
            ...prefs.email,
          },
        };
      }

      // Return defaults for new users
      return DEFAULT_PREFERENCES;
    } catch (error) {
      Logger.log(
        "error",
        "Error fetching notification preferences",
        {
          userId,
          error: error.message,
        },
        "NotificationService"
      );
      return DEFAULT_PREFERENCES;
    }
  }

  /**
   * Update user notification preferences
   * @param {string} userId - User ID
   * @param {Object} preferences - New preferences
   * @returns {Promise<boolean>} - Success status
   */
  static async updateUserPreferences(userId, preferences) {
    try {
      Logger.log(
        "info",
        "Updating user notification preferences",
        { userId },
        "NotificationService"
      );

      await setDoc(
        doc(db, "users", userId, "settings", "notifications"),
        {
          ...preferences,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      Logger.log(
        "info",
        "Successfully updated notification preferences",
        { userId },
        "NotificationService"
      );
      return true;
    } catch (error) {
      Logger.log(
        "error",
        "Error updating notification preferences",
        {
          userId,
          error: error.message,
        },
        "NotificationService"
      );
      throw error;
    }
  }

  /**
   * Check if user should receive a specific notification
   * @param {string} userId - User ID
   * @param {string} notificationType - Notification type
   * @param {Object} options - Additional options
   * @returns {Promise<boolean>} - Whether to send notification
   */
  static async shouldSendNotification(userId, notificationType, options = {}) {
    try {
      // Check if notifications are globally enabled
      if (!FeatureFlags.notifications?.enabled) {
        return false;
      }

      // Critical notifications always go through (safety override)
      const category = NOTIFICATION_CATEGORY_MAP[notificationType];
      if (category === NOTIFICATION_CATEGORIES.CRITICAL) {
        return true;
      }

      const preferences = await this.getUserPreferences(userId);

      // Check if this specific notification type is enabled
      if (!preferences.email[notificationType]) {
        return false;
      }

      // Check quiet hours if enabled
      if (preferences.quietHours?.enabled && !options.ignoreQuietHours) {
        const now = new Date();
        const currentTime = now.toTimeString().slice(0, 5); // HH:MM format

        if (this.isInQuietHours(currentTime, preferences.quietHours)) {
          // Queue for later unless it's critical
          return category === NOTIFICATION_CATEGORIES.CRITICAL;
        }
      }

      return true;
    } catch (error) {
      Logger.log(
        "error",
        "Error checking notification preferences",
        {
          userId,
          notificationType,
          error: error.message,
        },
        "NotificationService"
      );

      // Default to sending critical notifications on error
      const category = NOTIFICATION_CATEGORY_MAP[notificationType];
      return category === NOTIFICATION_CATEGORIES.CRITICAL;
    }
  }

  /**
   * Check if current time is in quiet hours
   * @param {string} currentTime - Current time in HH:MM format
   * @param {Object} quietHours - Quiet hours configuration
   * @returns {boolean} - Whether in quiet hours
   */
  static isInQuietHours(currentTime, quietHours) {
    const { start, end } = quietHours;

    // Handle same-day quiet hours (e.g., 22:00 - 23:59)
    if (start <= end) {
      return currentTime >= start && currentTime <= end;
    }

    // Handle overnight quiet hours (e.g., 22:00 - 08:00)
    return currentTime >= start || currentTime <= end;
  }

  /**
   * Send a notification (main entry point)
   * @param {string} userId - Recipient user ID
   * @param {string} notificationType - Type of notification
   * @param {Object} data - Notification data
   * @param {Object} options - Additional options
   * @returns {Promise<boolean>} - Success status
   */
  static async sendNotification(userId, notificationType, data, options = {}) {
    try {
      Logger.log(
        "info",
        "Processing notification request",
        {
          userId,
          notificationType,
          immediate: options.immediate,
        },
        "NotificationService"
      );

      // Check if we should send this notification
      if (
        !options.immediate &&
        !(await this.shouldSendNotification(userId, notificationType, options))
      ) {
        Logger.log(
          "info",
          "Notification skipped due to user preferences",
          {
            userId,
            notificationType,
          },
          "NotificationService"
        );
        return false;
      }

      // Get user data for email address
      const userDoc = await getDoc(doc(db, "users", userId));
      if (!userDoc.exists()) {
        throw new Error("User not found");
      }

      const userData = userDoc.data();
      if (!userData.email) {
        throw new Error("User email not found");
      }

      // Add user data to notification data
      const notificationData = {
        ...data,
        recipientEmail: userData.email,
        recipientName: userData.name || userData.displayName || "User",
      };

      // Determine if we should send immediately or queue
      const preferences = await this.getUserPreferences(userId);
      const shouldQueue =
        preferences.frequency !== "immediate" &&
        NOTIFICATION_CATEGORY_MAP[notificationType] !==
          NOTIFICATION_CATEGORIES.CRITICAL &&
        !options.immediate;

      if (shouldQueue) {
        return await this.queueNotification(
          userId,
          notificationType,
          notificationData,
          preferences.frequency
        );
      } else {
        return await this.sendImmediateNotification(
          notificationType,
          notificationData
        );
      }
    } catch (error) {
      Logger.log(
        "error",
        "Error sending notification",
        {
          userId,
          notificationType,
          error: error.message,
        },
        "NotificationService"
      );
      throw error;
    }
  }

  /**
   * Send notification immediately via Firebase Function
   * @param {string} notificationType - Type of notification
   * @param {Object} data - Notification data
   * @returns {Promise<boolean>} - Success status
   */
  static async sendImmediateNotification(notificationType, data) {
    try {
      // Import Firebase Functions (lazy loading to avoid import issues)
      const { httpsCallable } = await import("firebase/functions");
      const { functions } = await import("@/firebase");

      // Map notification types to email template types
      const templateTypeMap = {
        [NOTIFICATION_TYPES.LESSON_BOOKED]: "lessonBooked",
        [NOTIFICATION_TYPES.LESSON_CANCELLED]: "lessonCancelled",
        [NOTIFICATION_TYPES.LESSON_REMINDER_24H]: "lessonReminder",
        [NOTIFICATION_TYPES.LESSON_REMINDER_2H]: "lessonReminder",
        [NOTIFICATION_TYPES.LESSON_REMINDER_30M]: "lessonReminder",
        [NOTIFICATION_TYPES.PAYMENT_SUCCESSFUL]: "paymentSuccessful",
        [NOTIFICATION_TYPES.PAYMENT_FAILED]: "paymentFailed",
        [NOTIFICATION_TYPES.NEW_MESSAGE]: "newMessage",
        // Add more mappings as needed
      };

      const templateType = templateTypeMap[notificationType];
      if (!templateType) {
        throw new Error(
          `No email template mapped for notification type: ${notificationType}`
        );
      }

      // Call Firebase Function to send email
      const sendNotificationEmail = httpsCallable(
        functions,
        "sendNotificationEmail"
      );
      const result = await sendNotificationEmail({
        templateType,
        toEmail: data.recipientEmail,
        data,
      });

      Logger.log(
        "info",
        "Immediate notification sent successfully",
        {
          notificationType,
          templateType,
          email: data.recipientEmail,
        },
        "NotificationService"
      );

      return result.data.success;
    } catch (error) {
      Logger.log(
        "error",
        "Error sending immediate notification",
        {
          notificationType,
          error: error.message,
        },
        "NotificationService"
      );
      throw error;
    }
  }

  /**
   * Queue notification for later delivery
   * @param {string} userId - User ID
   * @param {string} notificationType - Type of notification
   * @param {Object} data - Notification data
   * @param {string} frequency - Delivery frequency
   * @returns {Promise<boolean>} - Success status
   */
  static async queueNotification(userId, notificationType, data, frequency) {
    try {
      const scheduledFor = this.calculateNextDeliveryTime(frequency);

      await addDoc(collection(db, "notificationQueue"), {
        userId,
        notificationType,
        data,
        frequency,
        scheduledFor,
        status: "queued",
        attempts: 0,
        createdAt: serverTimestamp(),
      });

      Logger.log(
        "info",
        "Notification queued successfully",
        {
          userId,
          notificationType,
          frequency,
          scheduledFor,
        },
        "NotificationService"
      );

      return true;
    } catch (error) {
      Logger.log(
        "error",
        "Error queuing notification",
        {
          userId,
          notificationType,
          error: error.message,
        },
        "NotificationService"
      );
      throw error;
    }
  }

  /**
   * Calculate next delivery time based on frequency
   * @param {string} frequency - Delivery frequency
   * @returns {Date} - Next delivery time
   */
  static calculateNextDeliveryTime(frequency) {
    const now = new Date();

    switch (frequency) {
      case "daily": {
        // Send at 9 AM next day
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(9, 0, 0, 0);
        return tomorrow;
      }

      case "weekly": {
        // Send at 9 AM next Monday
        const nextWeek = new Date(now);
        const daysUntilMonday = (7 - now.getDay() + 1) % 7 || 7;
        nextWeek.setDate(nextWeek.getDate() + daysUntilMonday);
        nextWeek.setHours(9, 0, 0, 0);
        return nextWeek;
      }

      default:
        // Immediate
        return now;
    }
  }

  /**
   * Get notification history for a user
   * @param {string} userId - User ID
   * @param {number} limitCount - Number of notifications to fetch
   * @returns {Promise<Array>} - Notification history
   */
  static async getNotificationHistory(userId, limitCount = 50) {
    try {
      const q = query(
        collection(db, "notificationHistory"),
        where("userId", "==", userId),
        orderBy("sentAt", "desc"),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const notifications = [];

      querySnapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return notifications;
    } catch (error) {
      Logger.log(
        "error",
        "Error fetching notification history",
        {
          userId,
          error: error.message,
        },
        "NotificationService"
      );
      throw error;
    }
  }

  /**
   * Get notification categories with their notification types
   * @returns {Object} - Categories with notification types
   */
  static getNotificationCategories() {
    const categories = {};

    Object.entries(NOTIFICATION_CATEGORY_MAP).forEach(([type, category]) => {
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(type);
    });

    return categories;
  }

  /**
   * Initialize default preferences for a new user
   * @param {string} userId - User ID
   * @returns {Promise<boolean>} - Success status
   */
  static async initializeUserPreferences(userId) {
    try {
      await this.updateUserPreferences(userId, DEFAULT_PREFERENCES);

      Logger.log(
        "info",
        "Initialized default notification preferences",
        { userId },
        "NotificationService"
      );
      return true;
    } catch (error) {
      Logger.log(
        "error",
        "Error initializing notification preferences",
        {
          userId,
          error: error.message,
        },
        "NotificationService"
      );
      throw error;
    }
  }
}

export default NotificationService;
